import { ChecklistItem, PriorityLevel } from '../types/api';
import { HUGE_ESTIMATED_TIME_TO_DONE_MINUTES } from './pasoTags';

export type PasoSortMode = 'manual' | 'time' | 'wsjf';

export interface WsjfGroupedItems {
  key: string;
  label: string;
  items: ChecklistItem[];
}

interface ItemWithIndex {
  item: ChecklistItem;
  originalIndex: number;
}

type NullablePriority = PriorityLevel | null;

const PRIORITY_RANK: Record<PriorityLevel, number> = {
  high: 0,
  moderate: 1,
  low: 2,
};

function getPriorityValue(value?: PriorityLevel): NullablePriority {
  return value ?? null;
}

function parseDueDate(dueDate?: string): number {
  if (!dueDate) return Number.MAX_SAFE_INTEGER;
  const value = Date.parse(`${dueDate}T00:00:00`);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
}

function estimatedMinutes(item: ChecklistItem): number {
  const raw = item.tags?.special?.estimatedTimeToDoneMinutes;
  if (raw === undefined || raw === null) {
    return Number.MAX_SAFE_INTEGER;
  }
  if (raw === HUGE_ESTIMATED_TIME_TO_DONE_MINUTES) {
    return Number.MAX_SAFE_INTEGER;
  }
  return raw;
}

function wsjfBucketRank(cost: NullablePriority, value: NullablePriority): number {
  // Conservative ordering choice requested by product:
  // unknown values are ranked immediately after high/high because they may be highs.
  if (cost === 'high' && value === 'high') return 0;
  if (!cost || !value) return 1;
  if (cost === 'high' && value === 'moderate') return 2;
  if (cost === 'high' && value === 'low') return 3;
  if (cost === 'moderate' && value === 'high') return 4;
  if (cost === 'moderate' && value === 'moderate') return 5;
  if (cost === 'moderate' && value === 'low') return 6;
  if (cost === 'low' && value === 'high') return 7;
  if (cost === 'low' && value === 'moderate') return 8;
  if (cost === 'low' && value === 'low') return 9;
  return 10;
}

function wsjfPairRank(cost: NullablePriority, value: NullablePriority): number {
  const costRank = cost ? PRIORITY_RANK[cost] : 3;
  const valueRank = value ? PRIORITY_RANK[value] : 3;
  return costRank * 10 + valueRank;
}

function labelForPriority(priority: NullablePriority): string {
  if (!priority) return '?';
  if (priority === 'high') return 'High';
  if (priority === 'moderate') return 'Medium';
  return 'Low';
}

function wsjfGroupLabel(cost: NullablePriority, value: NullablePriority): string {
  return `Neglect ${labelForPriority(cost)} / Value ${labelForPriority(value)}`;
}

function sortByTimeDueAndOriginal(items: ItemWithIndex[]): ItemWithIndex[] {
  return [...items].sort((left, right) => {
    const timeDelta = estimatedMinutes(left.item) - estimatedMinutes(right.item);
    if (timeDelta !== 0) return timeDelta;

    const dueDelta = parseDueDate(left.item.tags?.special?.dueDate) - parseDueDate(right.item.tags?.special?.dueDate);
    if (dueDelta !== 0) return dueDelta;

    return left.originalIndex - right.originalIndex;
  });
}

export function sortItemsByTimeOrder(items: ChecklistItem[]): ChecklistItem[] {
  const withIndex: ItemWithIndex[] = items.map((item, originalIndex) => ({ item, originalIndex }));
  return sortByTimeDueAndOriginal(withIndex).map(entry => entry.item);
}

export function sortItemsByWsjfOrder(items: ChecklistItem[]): ChecklistItem[] {
  const withIndex: ItemWithIndex[] = items.map((item, originalIndex) => ({ item, originalIndex }));

  return [...withIndex]
    .sort((left, right) => {
      const leftCost = getPriorityValue(left.item.tags?.special?.opportunityCostIfNotDone);
      const leftValue = getPriorityValue(left.item.tags?.special?.valueIfDone);
      const rightCost = getPriorityValue(right.item.tags?.special?.opportunityCostIfNotDone);
      const rightValue = getPriorityValue(right.item.tags?.special?.valueIfDone);

      const bucketDelta = wsjfBucketRank(leftCost, leftValue) - wsjfBucketRank(rightCost, rightValue);
      if (bucketDelta !== 0) return bucketDelta;

      const pairDelta = wsjfPairRank(leftCost, leftValue) - wsjfPairRank(rightCost, rightValue);
      if (pairDelta !== 0) return pairDelta;

      const timeDelta = estimatedMinutes(left.item) - estimatedMinutes(right.item);
      if (timeDelta !== 0) return timeDelta;

      const dueDelta = parseDueDate(left.item.tags?.special?.dueDate) - parseDueDate(right.item.tags?.special?.dueDate);
      if (dueDelta !== 0) return dueDelta;

      return left.originalIndex - right.originalIndex;
    })
    .map(entry => entry.item);
}

export function groupWsjfItems(items: ChecklistItem[]): WsjfGroupedItems[] {
  const sortedItems = sortItemsByWsjfOrder(items);
  const grouped = new Map<string, WsjfGroupedItems>();

  sortedItems.forEach(item => {
    const cost = getPriorityValue(item.tags?.special?.opportunityCostIfNotDone);
    const value = getPriorityValue(item.tags?.special?.valueIfDone);
    const key = `${cost ?? '?'}|${value ?? '?'}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        label: wsjfGroupLabel(cost, value),
        items: [],
      });
    }

    grouped.get(key)!.items.push(item);
  });

  return Array.from(grouped.values());
}

export function hasUnknownWsjfPriority(item: ChecklistItem, kind: 'cost' | 'value'): boolean {
  if (kind === 'cost') {
    return !item.tags?.special?.opportunityCostIfNotDone;
  }
  return !item.tags?.special?.valueIfDone;
}
